/**
 * In-catalogue recipe editor (add-pos-recipes-and-cups §3 / §4) — lets an
 * article BE a recipe (a sellable drink that depletes its ingredients at settle
 * instead of its own stock) and manage it right from the article's catalog
 * detail pane, so recipes live in categories/groups like any product.
 *
 * View mode shows the ingredients + derived availability + projected COGS; edit
 * mode is a compact ingredient-row editor. Create (POST), replace ingredients
 * (PUT) and remove (DELETE) all go through ``/api/recipes`` and then call
 * ``onChanged`` so the catalog (badges, availability) refreshes.
 *
 * FUNCTIONAL scaffold — plain AntD wired to the real API; the design pass
 * restyles it.
 */

import { useMemo, useState } from "react";

import {
  App as AntApp,
  Button,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";

import { ApiError, api, type RecipeRow } from "../../api/client";
import {
  UNIT_LABEL,
  UNIT_OPTIONS,
  defaultUnitType,
  type RecipeUnitType,
} from "../../lib/recipeUnits";
import { formatCents } from "../../utils/dateFormat";

const { Text } = Typography;

export interface ConsumableOption {
  value: number;
  label: string;
  unitType: RecipeUnitType;
}

interface DraftIngredient {
  consumable_chain_root_id: number | null;
  amount: number | null;
  unit_type: RecipeUnitType;
}

const EMPTY: DraftIngredient = {
  consumable_chain_root_id: null,
  amount: null,
  unit_type: "liters",
};

export function RecipeSection({
  chainRootId,
  recipe,
  consumables,
  nameByRoot,
  onChanged,
}: {
  chainRootId: number;
  /** The recipe for this article's chain root, or null if it isn't a recipe. */
  recipe: RecipeRow | null;
  /** All pickable consumables (one per chain root) for the ingredient editor. */
  consumables: ConsumableOption[];
  nameByRoot: (id: number) => string;
  onChanged: () => void;
}) {
  const { message } = AntApp.useApp();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DraftIngredient[]>([]);
  const [saving, setSaving] = useState(false);

  // A recipe can't contain itself; drop this article from the picker.
  const options = useMemo(
    () => consumables.filter((c) => c.value !== chainRootId),
    [consumables, chainRootId],
  );
  const unitByRoot = useMemo(() => {
    const m = new Map<number, RecipeUnitType>();
    for (const c of consumables) m.set(c.value, c.unitType);
    return m;
  }, [consumables]);

  const startEdit = () => {
    setDraft(
      recipe && recipe.ingredients.length
        ? recipe.ingredients.map((i) => ({
            consumable_chain_root_id: i.consumable_chain_root_id,
            amount: i.amount,
            unit_type: i.unit_type,
          }))
        : [{ ...EMPTY }],
    );
    setEditing(true);
  };

  const setRow = (idx: number, patch: Partial<DraftIngredient>) =>
    setDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const collect = () => {
    const out: { consumable_chain_root_id: number; amount: number; unit_type: RecipeUnitType }[] = [];
    const seen = new Set<number>();
    for (const r of draft) {
      if (r.consumable_chain_root_id == null || r.amount == null || r.amount <= 0) {
        message.warning("Every ingredient needs a consumable and a positive amount.");
        return null;
      }
      if (seen.has(r.consumable_chain_root_id)) {
        message.warning("Each consumable may appear only once.");
        return null;
      }
      seen.add(r.consumable_chain_root_id);
      out.push({
        consumable_chain_root_id: r.consumable_chain_root_id,
        amount: r.amount,
        unit_type: r.unit_type,
      });
    }
    if (out.length === 0) {
      message.warning("A recipe needs at least one ingredient.");
      return null;
    }
    return out;
  };

  const save = async () => {
    const ingredients = collect();
    if (!ingredients) return;
    setSaving(true);
    try {
      if (recipe) {
        await api.replaceRecipeIngredients(recipe.id, ingredients);
        message.success("Recipe updated");
      } else {
        await api.createRecipe({
          article_chain_root_id: chainRootId,
          ingredients,
        });
        message.success("Recipe created");
      }
      setEditing(false);
      onChanged();
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!recipe) return;
    try {
      await api.deleteRecipe(recipe.id);
      message.success("Recipe removed");
      onChanged();
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : "Remove failed");
    }
  };

  // --- edit mode ----------------------------------------------------------
  if (editing) {
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        {draft.map((ing, idx) => (
          <Space key={idx} wrap align="start">
            <Select<number>
              showSearch
              placeholder="Consumable"
              style={{ width: 200 }}
              value={ing.consumable_chain_root_id ?? undefined}
              options={options}
              optionFilterProp="label"
              onChange={(v) =>
                setRow(idx, {
                  consumable_chain_root_id: v,
                  unit_type: unitByRoot.get(v) ?? defaultUnitType(),
                })
              }
            />
            <InputNumber
              placeholder="Amount"
              style={{ width: 96 }}
              min={1}
              value={ing.amount ?? undefined}
              onChange={(v) => setRow(idx, { amount: v == null ? null : Number(v) })}
            />
            <Select<RecipeUnitType>
              style={{ width: 120 }}
              value={ing.unit_type}
              options={UNIT_OPTIONS}
              onChange={(v) => setRow(idx, { unit_type: v })}
            />
            <Button
              danger
              disabled={draft.length <= 1}
              onClick={() => setDraft((rows) => rows.filter((_, i) => i !== idx))}
            >
              −
            </Button>
          </Space>
        ))}
        <Space>
          <Button onClick={() => setDraft((rows) => [...rows, { ...EMPTY }])}>
            + Ingredient
          </Button>
          <Button type="primary" loading={saving} onClick={() => void save()}>
            {recipe ? "Save ingredients" : "Create recipe"}
          </Button>
          <Button onClick={() => setEditing(false)}>Cancel</Button>
        </Space>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Amounts are base units: mL (volume), g (weight), whole units (count).
        </Text>
      </Space>
    );
  }

  // --- view mode ----------------------------------------------------------
  if (!recipe) {
    return (
      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Make this article a recipe — selling it then depletes its ingredients
          (not its own stock) and its availability is derived from them.
        </Text>
        <Button onClick={startEdit}>+ Make this a recipe</Button>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Space wrap>
        <Tag color="purple">recipe</Tag>
        {recipe.available != null && (
          <Tag color={recipe.available <= 0 ? "red" : recipe.available < 10 ? "orange" : "green"}>
            {recipe.available} servings
          </Tag>
        )}
        {recipe.cogs_cents != null && (
          <Text type="secondary">COGS {formatCents(recipe.cogs_cents)}/serving</Text>
        )}
      </Space>
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        {recipe.ingredients.map((i) => (
          <Text key={i.id} style={{ fontSize: 12 }}>
            {nameByRoot(i.consumable_chain_root_id)} ·{" "}
            <b>
              {i.amount} {UNIT_LABEL[i.unit_type]}
            </b>
          </Text>
        ))}
      </Space>
      <Space>
        <Button size="small" onClick={startEdit}>
          Edit ingredients
        </Button>
        <Popconfirm
          title="Remove the recipe?"
          description="The article stays; it just stops depleting ingredients."
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => void remove()}
        >
          <Button size="small" danger>
            Remove recipe
          </Button>
        </Popconfirm>
      </Space>
    </Space>
  );
}

export default RecipeSection;
